import React, { useState, useEffect } from 'react'

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000'

const DOC_TYPE_COLORS = {
  'Bank Statement':      { bg: '#E8F0FE', color: '#185FA5' },
  'Receipts / Invoices': { bg: '#EAF3DE', color: '#3B6D11' },
  'Payroll Records':     { bg: '#FFF8E6', color: '#854F0B' },
  'VAT Records':         { bg: '#F2EEFF', color: '#5B3AAA' },
  'Other Documents':     { bg: '#F0F0F0', color: '#555555' },
}

const STATUS_COLORS = {
  'pending_review': { bg: '#FFF8E6', color: '#854F0B', label: 'Pending Review' },
  'reviewed':       { bg: '#EAF3DE', color: '#3B6D11', label: 'Reviewed' },
  'processed':      { bg: '#E8F0FE', color: '#185FA5', label: 'Processed' },
}

function timeAgo(dateStr) {
  const now = new Date()
  const date = new Date(dateStr)
  const diff = Math.floor((now - date) / 1000)
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff/60)}m ago`
  if (diff < 86400) return `${Math.floor(diff/3600)}h ago`
  return `${Math.floor(diff/86400)}d ago`
}

function formatSize(bytes) {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1048576) return `${(bytes/1024).toFixed(1)} KB`
  return `${(bytes/1048576).toFixed(1)} MB`
}

export default function DocumentInbox() {
  const [uploads, setUploads]     = useState([])
  const [loading, setLoading]     = useState(true)
  const [error, setError]         = useState('')
  const [search, setSearch]       = useState('')
  const [typeFilter, setTypeFilter] = useState('All')
  const [selected, setSelected]   = useState(null)
  const [refreshing, setRefreshing] = useState(false)

  const fetchUploads = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true)
    try {
      const res = await fetch(`${API}/api/portal/uploads`)
      const data = await res.json()
      setUploads(data.uploads || [])
      setError('')
    } catch (e) {
      setError('Could not load uploads — make sure the backend is running')
    }
    setLoading(false)
    setRefreshing(false)
  }

  useEffect(() => { fetchUploads() }, [])

  const filtered = uploads.filter(u => {
    const matchSearch = !search ||
      u.client_name?.toLowerCase().includes(search.toLowerCase()) ||
      u.client_email?.toLowerCase().includes(search.toLowerCase()) ||
      u.upload_ref?.toLowerCase().includes(search.toLowerCase()) ||
      u.filename?.toLowerCase().includes(search.toLowerCase())
    const matchType = typeFilter === 'All' || u.document_type === typeFilter
    return matchSearch && matchType
  })

  const pending   = uploads.filter(u => u.status === 'pending_review').length
  const reviewed  = uploads.filter(u => u.status === 'reviewed').length
  const processed = uploads.filter(u => u.status === 'processed').length

  const downloadFile = (upload) => {
    window.open(`${API}/api/portal/download/${upload.upload_ref}/${upload.filename}`, '_blank')
  }

  return (
    <div style={{ padding: 20, maxWidth: 1100 }}>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total received',    val: uploads.length, color: '#0B1929' },
          { label: 'Pending review',    val: pending,        color: '#C9973A' },
          { label: 'Reviewed',          val: reviewed,       color: '#185FA5' },
          { label: 'Processed',         val: processed,      color: '#3B6D11' },
        ].map(s => (
          <div key={s.label} style={{ background: 'white', border: '0.5px solid #e0e3e9', borderRadius: 12, padding: '14px 16px', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 26, fontWeight: 500 }}>{s.val}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by client name, email, reference or filename..."
          style={{ flex: 1, minWidth: 200, border: '0.5px solid #ccc', borderRadius: 8, padding: '8px 12px', fontSize: 12, fontFamily: 'inherit', outline: 'none' }}
          onFocus={e => e.target.style.borderColor = '#C9973A'}
          onBlur={e => e.target.style.borderColor = '#ccc'}
        />
        <div style={{ display: 'flex', gap: 3 }}>
          {['All', 'Bank Statement', 'Receipts / Invoices', 'Payroll Records', 'VAT Records', 'Other Documents'].map(t => (
            <button key={t} onClick={() => setTypeFilter(t)}
              style={{ padding: '6px 10px', borderRadius: 7, border: '0.5px solid #ddd', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', background: typeFilter === t ? '#0B1929' : 'white', color: typeFilter === t ? '#C9973A' : '#666', whiteSpace: 'nowrap' }}>
              {t === 'All' ? 'All types' : t}
            </button>
          ))}
        </div>
        <button onClick={() => fetchUploads(true)}
          style={{ padding: '7px 12px', borderRadius: 7, border: '0.5px solid #C9973A', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', background: 'rgba(201,151,58,0.08)', color: '#7A4A10' }}>
          {refreshing ? '⏳' : '↺'} Refresh
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 380px' : '1fr', gap: 14 }}>

        {/* Upload list */}
        <div style={{ background: 'white', border: '0.5px solid #e0e3e9', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: '11px 16px', background: '#f7f8fa', borderBottom: '0.5px solid #e0e3e9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, fontWeight: 500 }}>Client uploads</span>
            <span style={{ fontSize: 11, color: '#888' }}>{filtered.length} document{filtered.length !== 1 ? 's' : ''}</span>
          </div>

          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#aaa' }}>
              <div style={{ fontSize: 13 }}>Loading uploads...</div>
            </div>
          ) : error ? (
            <div style={{ padding: 20 }}>
              <div style={{ background: '#FFF0F0', borderRadius: 8, padding: '12px 16px', fontSize: 12, color: '#A32D2D' }}>
                ⚠ {error}
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#aaa' }}>
              <div style={{ fontSize: 32, marginBottom: 10, opacity: .3 }}>📭</div>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#555', marginBottom: 4 }}>
                {uploads.length === 0 ? 'No documents received yet' : 'No results match your search'}
              </div>
              <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                {uploads.length === 0
                  ? `Share sunshoos-v2.vercel.app/portal with clients to receive documents`
                  : 'Try a different search or filter'}
              </div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f7f8fa' }}>
                  {['Client', 'Document type', 'File', 'Received', 'Status', ''].map(h => (
                    <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '.06em', borderBottom: '0.5px solid #e0e3e9' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((u, i) => {
                  const dtc = DOC_TYPE_COLORS[u.document_type] || DOC_TYPE_COLORS['Other Documents']
                  const sc  = STATUS_COLORS[u.status] || STATUS_COLORS['pending_review']
                  const isSelected = selected?.upload_ref === u.upload_ref
                  return (
                    <tr key={u.upload_ref || i}
                      onClick={() => setSelected(isSelected ? null : u)}
                      style={{ cursor: 'pointer', background: isSelected ? '#f0f4ff' : 'white', borderBottom: '0.5px solid #e0e3e9' }}
                      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#fafafa' }}
                      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'white' }}
                    >
                      <td style={{ padding: '11px 14px' }}>
                        <div style={{ fontSize: 12, fontWeight: 500 }}>{u.client_name}</div>
                        <div style={{ fontSize: 10, color: '#888', marginTop: 1 }}>{u.client_email}</div>
                      </td>
                      <td style={{ padding: '11px 14px' }}>
                        <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 99, background: dtc.bg, color: dtc.color, fontWeight: 500 }}>{u.document_type}</span>
                      </td>
                      <td style={{ padding: '11px 14px' }}>
                        <div style={{ fontSize: 11, color: '#555', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.filename}</div>
                        <div style={{ fontSize: 10, color: '#aaa', marginTop: 1 }}>{formatSize(u.file_size)}</div>
                      </td>
                      <td style={{ padding: '11px 14px' }}>
                        <div style={{ fontSize: 11, color: '#555' }}>{timeAgo(u.uploaded_at)}</div>
                        <div style={{ fontSize: 10, color: '#aaa', fontFamily: 'monospace', marginTop: 1 }}>{u.upload_ref}</div>
                      </td>
                      <td style={{ padding: '11px 14px' }}>
                        <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 99, background: sc.bg, color: sc.color, fontWeight: 500 }}>{sc.label}</span>
                      </td>
                      <td style={{ padding: '11px 14px' }}>
                        <button onClick={e => { e.stopPropagation(); downloadFile(u) }}
                          style={{ fontSize: 10, padding: '4px 10px', borderRadius: 6, border: '0.5px solid #C9973A', background: 'rgba(201,151,58,0.07)', color: '#7A4A10', cursor: 'pointer', fontFamily: 'inherit' }}>
                          ⬇ Download
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Detail panel */}
        {selected && (
          <div style={{ background: 'white', border: '0.5px solid #e0e3e9', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '11px 16px', background: '#f7f8fa', borderBottom: '0.5px solid #e0e3e9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, fontWeight: 500 }}>Document detail</span>
              <button onClick={() => setSelected(null)}
                style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: '#aaa' }}>×</button>
            </div>

            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14, flex: 1 }}>

              {/* Reference */}
              <div style={{ background: '#f7f8fa', borderRadius: 10, padding: '12px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: '#aaa', marginBottom: 4 }}>Reference number</div>
                <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'monospace', color: '#0B1929', letterSpacing: '.1em' }}>{selected.upload_ref}</div>
              </div>

              {/* Client info */}
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: '#aaa', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8 }}>Client</div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{selected.client_name}</div>
                <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{selected.client_email}</div>
              </div>

              {/* Document info */}
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: '#aaa', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8 }}>Document</div>
                {[
                  ['Type',     selected.document_type],
                  ['Filename', selected.filename],
                  ['Size',     formatSize(selected.file_size)],
                  ['Received', new Date(selected.uploaded_at).toLocaleString('en-GB')],
                ].map(([label, val]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '0.5px solid #f0f0f0' }}>
                    <span style={{ fontSize: 11, color: '#888' }}>{label}</span>
                    <span style={{ fontSize: 11, color: '#1a1a2e', fontWeight: 500, maxWidth: 200, textAlign: 'right', wordBreak: 'break-all' }}>{val}</span>
                  </div>
                ))}
              </div>

              {/* Notes */}
              {selected.notes && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: '#aaa', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 }}>Client notes</div>
                  <div style={{ background: '#FFF8E6', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#7A4A10', lineHeight: 1.6 }}>
                    {selected.notes}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 'auto' }}>
                <button onClick={() => downloadFile(selected)}
                  style={{ background: '#0B1929', color: '#C9973A', border: 'none', borderRadius: 9, padding: '10px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                  ⬇ Download document
                </button>
                <button onClick={() => window.location.href = '/bank-pl'}
                  style={{ background: 'rgba(201,151,58,0.08)', border: '1px solid rgba(201,151,58,0.3)', borderRadius: 9, padding: '10px', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', color: '#7A4A10' }}>
                  ⬡ Process in Bank Stmt → P&L
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* How to share */}
      <div style={{ marginTop: 16, background: '#E8F0FE', borderRadius: 10, padding: '12px 16px', fontSize: 12, color: '#185FA5', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 16 }}>🔗</span>
        <div>
          <strong>Client upload link:</strong> Share <code style={{ background: 'rgba(24,95,165,0.1)', padding: '1px 6px', borderRadius: 4 }}>sunshoos-v2.vercel.app/portal</code> with any client — they can upload documents without creating an account.
        </div>
      </div>
    </div>
  )
}
